import { defineStore } from 'pinia';
import TablesAPI from 'dashboard/api/tables';
import TableFoldersAPI from 'dashboard/api/tableFolders';

// Tables — backed by /api/v1/accounts/:id/tables.
//
// Rows are never held wholesale: the grid pages them server-side, because a
// table is filtered and sorted over jsonb and pulling it all into the client
// would defeat that. `rowsMeta` carries the paging state the grid renders.
export const useTablesStore = defineStore('tables', {
  state: () => ({
    tables: [],
    folders: [],
    columns: [],
    rows: [],
    rowsMeta: { total: 0, page: 1, per_page: 25 },
    savedQueries: [],
    grants: [],
    subjects: { users: [], bot_users: [], teams: [] },
    isLoading: false,
  }),
  getters: {
    list: state => state.tables,
    getTable: state => id => state.tables.find(t => t.id === id),
    totalPages: state =>
      Math.ceil((state.rowsMeta.total || 0) / (state.rowsMeta.per_page || 25)),
  },
  actions: {
    async fetch() {
      const { data } = await TablesAPI.get();
      this.tables = data.payload || [];
    },
    async createTable(payload) {
      const { data } = await TablesAPI.create({ table: payload });
      this.tables = [...this.tables, data.payload];
      return data.payload;
    },
    async updateTable(id, payload) {
      const { data } = await TablesAPI.update(id, { table: payload });
      this.tables = this.tables.map(t => (t.id === id ? data.payload : t));
      return data.payload;
    },
    // Re-fetches one table, including its freshly-resolved
    // `current_user_permissions` — needed after a column mutation, since
    // that permission snapshot would otherwise stay stale in the store.
    async fetchTable(id) {
      const { data } = await TablesAPI.show(id);
      this.tables = this.tables.map(t => (t.id === id ? data.payload : t));
      return data.payload;
    },
    async deleteTable(id) {
      await TablesAPI.delete(id);
      this.tables = this.tables.filter(t => t.id !== id);
    },

    async fetchFolders() {
      const { data } = await TableFoldersAPI.get();
      this.folders = data.payload || [];
    },
    async createFolder(name) {
      const { data } = await TableFoldersAPI.create({ table_folder: { name } });
      this.folders = [...this.folders, data.payload];
      return data.payload;
    },
    async renameFolder(id, name) {
      const { data } = await TableFoldersAPI.update(id, {
        table_folder: { name },
      });
      this.folders = this.folders.map(f => (f.id === id ? data.payload : f));
    },
    // The server ungroups the folder's tables (FK nullify); mirror that here
    // rather than refetching every table.
    async deleteFolder(id) {
      await TableFoldersAPI.delete(id);
      this.folders = this.folders.filter(f => f.id !== id);
      this.tables = this.tables.map(t =>
        t.folder_id === id ? { ...t, folder_id: null } : t
      );
    },
    moveTable(tableId, folderId) {
      return this.updateTable(tableId, { folder_id: folderId });
    },

    async fetchColumns(tableId) {
      const { data } = await TablesAPI.getColumns(tableId);
      this.columns = data.payload || [];
      return this.columns;
    },
    async createColumn(tableId, column) {
      const { data } = await TablesAPI.createColumn(tableId, column);
      this.columns = [...this.columns, data.payload];
      return data.payload;
    },
    async updateColumn(tableId, columnId, column) {
      const { data } = await TablesAPI.updateColumn(tableId, columnId, column);
      this.columns = this.columns.map(c =>
        c.id === columnId ? data.payload : c
      );
      return data.payload;
    },
    async deleteColumn(tableId, columnId) {
      await TablesAPI.deleteColumn(tableId, columnId);
      this.columns = this.columns.filter(c => c.id !== columnId);
    },

    async fetchRows(tableId, params = {}) {
      this.isLoading = true;
      try {
        const { data } = await TablesAPI.getRows(tableId, params);
        this.rows = data.payload || [];
        this.rowsMeta = data.meta || this.rowsMeta;
      } finally {
        this.isLoading = false;
      }
    },
    async createRow(tableId, rowData) {
      const { data } = await TablesAPI.createRow(tableId, rowData);
      this.rows = [...this.rows, data.payload];
      return data.payload;
    },
    // Sends only the changed cells: the backend authorizes an update against
    // the columns it actually touches, so sending the whole row would demand
    // write permission on every column.
    async updateRow(tableId, rowId, changedCells) {
      const { data } = await TablesAPI.updateRow(tableId, rowId, changedCells);
      this.rows = this.rows.map(r => (r.id === rowId ? data.payload : r));
      return data.payload;
    },
    async deleteRow(tableId, rowId) {
      await TablesAPI.deleteRow(tableId, rowId);
      this.rows = this.rows.filter(r => r.id !== rowId);
    },
    async fetchRowHistory(tableId, rowId) {
      const { data } = await TablesAPI.rowHistory(tableId, rowId);
      return data.payload || [];
    },

    async fetchSavedQueries(tableId) {
      const { data } = await TablesAPI.getSavedQueries(tableId);
      this.savedQueries = data.payload || [];
    },
    async createSavedQuery(tableId, savedQuery) {
      const { data } = await TablesAPI.createSavedQuery(tableId, savedQuery);
      this.savedQueries = [...this.savedQueries, data.payload];
      return data.payload;
    },
    async updateSavedQuery(tableId, id, savedQuery) {
      const { data } = await TablesAPI.updateSavedQuery(
        tableId,
        id,
        savedQuery
      );
      this.savedQueries = this.savedQueries.map(q =>
        q.id === id ? data.payload : q
      );
      return data.payload;
    },
    async deleteSavedQuery(tableId, id) {
      await TablesAPI.deleteSavedQuery(tableId, id);
      this.savedQueries = this.savedQueries.filter(q => q.id !== id);
    },

    async fetchGrants(tableId) {
      const { data } = await TablesAPI.getGrants(tableId);
      this.grants = data.payload || [];
    },
    async fetchSubjects(tableId) {
      const { data } = await TablesAPI.getGrantSubjects(tableId);
      this.subjects = data.payload || { users: [], bot_users: [], teams: [] };
    },
    async createGrant(tableId, grant) {
      const { data } = await TablesAPI.createGrant(tableId, grant);
      this.grants = [...this.grants, data.payload];
      return data.payload;
    },
    async updateGrant(tableId, id, grant) {
      const { data } = await TablesAPI.updateGrant(tableId, id, grant);
      this.grants = this.grants.map(g => (g.id === id ? data.payload : g));
      return data.payload;
    },
    async deleteGrant(tableId, id) {
      await TablesAPI.deleteGrant(tableId, id);
      this.grants = this.grants.filter(g => g.id !== id);
    },

    async importSpreadsheet(tableId, file, mapping) {
      const { data } = await TablesAPI.import(tableId, file, mapping);
      return data.payload;
    },
    async fetchImportHeaders(tableId, file) {
      const { data } = await TablesAPI.importHeaders(tableId, file);
      return data.payload.headers;
    },
  },
});
